declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  isExpanded: boolean;
  themeParams: {
    bg_color?: string;
    text_color?: string;
    button_color?: string;
    button_text_color?: string;
  };
  ready(): void;
  expand(): void;
  close(): void;
  MainButton: {
    text: string;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
    selectionChanged(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

const DESKTOP_PLATFORMS = new Set(["tdesktop", "web", "macos", "windows", "linux", "weba"]);
const MOBILE_PLATFORMS = new Set(["android", "ios", "android_x"]);

function applyPlatformClasses(platform: string) {
  document.documentElement.dataset.platform = platform;
  if (DESKTOP_PLATFORMS.has(platform)) {
    document.documentElement.classList.add("platform-desktop");
  }
  if (MOBILE_PLATFORMS.has(platform)) {
    document.documentElement.classList.add("platform-mobile");
  }
}

async function lockPortraitOrientation() {
  if (!document.documentElement.classList.contains("platform-mobile")) return;

  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (orientation: "portrait-primary" | "portrait") => Promise<void>;
    };
    await orientation?.lock?.("portrait-primary");
  } catch {
    // Browser / Telegram may deny lock — OrientationGuard covers landscape UI.
  }
}

// Read from window on each call — SDK may load after module import
function tg() {
  return window.Telegram?.WebApp;
}

export function getTelegramInitData(): string {
  return tg()?.initData ?? "";
}

export function getTelegramUser() {
  return tg()?.initDataUnsafe?.user ?? null;
}

export function hapticLight() {
  tg()?.HapticFeedback?.impactOccurred("light");
}

export function hapticImpact(style: "light" | "medium" | "heavy" | "rigid" | "soft") {
  tg()?.HapticFeedback?.impactOccurred(style);
}

export function hapticSuccess() {
  tg()?.HapticFeedback?.notificationOccurred("success");
}

export function hapticError() {
  tg()?.HapticFeedback?.notificationOccurred("error");
}

export function initTelegram() {
  const app = tg();
  if (app) {
    applyPlatformClasses(app.platform ?? "unknown");
    app.ready();
    app.expand();
    void lockPortraitOrientation();
    return;
  }

  applyPlatformClasses("browser");
}

export const IS_DEV = import.meta.env.DEV;

/** When true, show the debug button in UI (VITE_DEBUG=true in .env). */
export const IS_DEBUG = import.meta.env.VITE_DEBUG === "true" || IS_DEV;
